import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, ShoppingCart, Package2, Share2, Send, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [productStats, setProductStats] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchProduct();
    fetchCompanySettings();
    fetchReviews();
    fetchProductStats();
    
    // Load TikTok embed script
    if (!document.querySelector('script[src*="tiktok.com/embed"]')) {
      const tiktokScript = document.createElement('script');
      tiktokScript.src = 'https://www.tiktok.com/embed.js';
      tiktokScript.async = true;
      document.body.appendChild(tiktokScript);
    }
    
    // Load Instagram embed script
    if (!document.querySelector('script[src*="instagram.com/embed"]')) {
      const instagramScript = document.createElement('script');
      instagramScript.src = 'https://www.instagram.com/embed.js';
      instagramScript.async = true;
      document.body.appendChild(instagramScript);
    }
  }, [id]);

  // Refetch company settings whenever location changes (navigation)
  useEffect(() => {
    // Fetch fresh data - timestamp will update if logo changed
    fetchCompanySettings();
  }, [location.pathname]);

  // Refetch company settings when page becomes visible (for when user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchCompanySettings();
      }
    };

    const handleFocus = () => {
      fetchCompanySettings();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Process embeds when product data loads or changes
  useEffect(() => {
    if (product) {
      // Process Instagram embeds
      if (window.instgrm) {
        window.instgrm.Embeds.process();
      }
      
      // Process TikTok embeds (with delay to ensure DOM is ready)
      setTimeout(() => {
        if (window.tiktok) {
          window.tiktok.embed.process();
        }
      }, 100);
    }
  }, [product]);

  const fetchProduct = async () => {
    try {
      // For now, fetch from all products and filter
      const response = await axios.get(`${API}/public/products`);
      const foundProduct = response.data.find(p => p.id === id);
      setProduct(foundProduct);
      setCurrentImageIndex(0); // Reset to first image when product loads
    } catch (error) {
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/public/settings/company`);
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Failed to load company settings:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/product/${id}`);
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    }
  };

  const fetchProductStats = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}/stats`);
      setProductStats(response.data);
    } catch (error) {
      console.error('Failed to load product stats:', error);
    }
  };

  const addToCart = () => {
    // Get existing cart from localStorage
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    
    const existingItem = existingCart.find(item => item.product_id === product.id);
    
    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      existingCart.push({
        product_id: product.id,
        quantity,
        product
      });
    }
    
    localStorage.setItem('cart', JSON.stringify(existingCart));
    toast.success('Added to cart');
    navigate('/?cart=open');
  };

  const handleShare = (platform) => {
    const productUrl = `${window.location.origin}/product/${product.id}`;
    const message = `Check out this product: ${product.name}\nPrice: ${formatCurrency(product.price)}\n${productUrl}`;
    
    if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    } else if (platform === 'viber') {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(message);
        toast.info('Product info copied! Open Viber to share.');
      }
      window.open('viber://forward?text=' + encodeURIComponent(message), '_blank');
    } else if (platform === 'copy') {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(productUrl);
        toast.success('Product link copied to clipboard!');
      }
    }
    setShowShareDialog(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Package2 className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Product Not Found</h2>
          <Button onClick={() => navigate('/')}>Back to Store</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Dynamic Meta Tags for Social Media Sharing */}
      <Helmet>
        <title>{product.name} - {companySettings?.company_name || 'AN-NISA Nexus'}</title>
        <meta name="description" content={product.description || `${product.name} - ${formatCurrency(product.price)}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content={`${product.name} - ${companySettings?.company_name || 'ASSETCO'}`} />
        <meta property="og:description" content={product.description || `${product.name} - Price: ${formatCurrency(product.price)}`} />
        <meta property="og:image" content={
          product.images && product.images.length > 0 && product.images[0].startsWith('http')
            ? product.images[0]
            : `${window.location.origin}/images/rufiyaa-symbol.png`
        } />
        <meta property="og:image:secure_url" content={
          product.images && product.images.length > 0 && product.images[0].startsWith('http')
            ? product.images[0]
            : `${window.location.origin}/images/rufiyaa-symbol.png`
        } />
        <meta property="og:image:alt" content={product.name} />
        <meta property="og:site_name" content={companySettings?.company_name || 'ASSETCO'} />
        <meta property="product:price:amount" content={product.price} />
        <meta property="product:price:currency" content={product.currency || 'USD'} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={window.location.href} />
        <meta name="twitter:title" content={`${product.name} - ${companySettings?.company_name || 'ASSETCO'}`} />
        <meta name="twitter:description" content={product.description || `${product.name} - Price: ${formatCurrency(product.price)}`} />
        <meta name="twitter:image" content={
          product.images && product.images.length > 0 && product.images[0].startsWith('http')
            ? product.images[0]
            : `${window.location.origin}/images/rufiyaa-symbol.png`
        } />
        <meta name="twitter:image:alt" content={product.name} />
        
        {/* WhatsApp & Other */}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/png" />
      </Helmet>

      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Store
              </Button>
            </div>
            <a href="/" className="flex items-center gap-2">
              {companySettings?.website_logo ? (
                <img 
                  src={companySettings.website_logo} 
                  alt="Logo" 
                  className="w-10 h-10 object-contain" 
                />
              ) : (
                <Package2 className="w-8 h-8 text-emerald-600" />
              )}
              <h1 className="text-2xl font-bold text-slate-900">{companySettings?.company_name || 'AN-NISA Nexus'}</h1>
            </a>
          </div>
        </div>
      </nav>

      {/* Product Detail */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Image Slider */}
          <div className="space-y-4">
            {/* Main Image with Navigation */}
            <div className="relative aspect-square bg-white rounded-2xl border-2 border-slate-200 flex items-center justify-center overflow-hidden shadow-lg">
              {product.images && product.images.length > 0 ? (
                <>
                  <img
                    src={product.images[currentImageIndex]}
                    alt={`${product.name} - Image ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Navigation Arrows (only show if multiple images) */}
                  {product.images.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentImageIndex((prev) => 
                          prev === 0 ? product.images.length - 1 : prev - 1
                        )}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="w-6 h-6 text-slate-700" />
                      </button>
                      
                      <button
                        onClick={() => setCurrentImageIndex((prev) => 
                          prev === product.images.length - 1 ? 0 : prev + 1
                        )}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                        aria-label="Next image"
                      >
                        <ChevronRight className="w-6 h-6 text-slate-700" />
                      </button>
                      
                      {/* Image Counter */}
                      <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                        {currentImageIndex + 1} / {product.images.length}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <Package2 className="w-32 h-32 text-slate-400" />
              )}
            </div>
            
            {/* Image Thumbnails */}
            {product.images && product.images.length > 1 && (
              <div className="grid grid-cols-5 gap-3">
                {product.images.map((image, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`aspect-square bg-white rounded-lg border-2 flex items-center justify-center overflow-hidden transition-all ${
                      currentImageIndex === idx 
                        ? 'border-emerald-500 ring-2 ring-emerald-300 scale-105' 
                        : 'border-slate-200 hover:border-emerald-300'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">{product.name}</h1>
              <p className="text-sm text-slate-600">SKU: {product.sku}</p>
            </div>

            <div className="flex items-baseline gap-4">
              <p className="text-5xl font-bold text-emerald-600">{formatCurrency(product.price)}</p>
              {product.quantity > 0 ? (
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full">
                  In Stock ({product.quantity} available)
                </span>
              ) : (
                <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-semibold rounded-full">
                  Out of Stock
                </span>
              )}
            </div>

            {product.description && (
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-3">Description</h2>
                <p className="text-slate-700 leading-relaxed">{product.description}</p>
              </div>
            )}

            <div className="space-y-4 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-4">
                <label className="text-slate-700 font-medium">Quantity:</label>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="h-10 w-10"
                  >
                    -
                  </Button>
                  <span className="w-16 text-center font-semibold text-lg">{quantity}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQuantity(Math.min(product.quantity, quantity + 1))}
                    className="h-10 w-10"
                    disabled={quantity >= product.quantity}
                  >
                    +
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex gap-3">
                  <Button
                    onClick={addToCart}
                    disabled={product.quantity === 0}
                    className="flex-1 h-14 bg-slate-200 hover:bg-slate-300 text-slate-900 text-lg font-semibold shadow-lg"
                    data-testid="add-to-cart-detail-button"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart
                  </Button>
                  <Button
                    onClick={() => setShowShareDialog(true)}
                    variant="outline"
                    className="h-14 px-6 border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  >
                    <Share2 className="w-5 h-5 mr-2" />
                    Share
                  </Button>
                </div>
                <Button
                  onClick={() => {
                    addToCart();
                    // Navigate to homepage with cart open after a short delay
                    setTimeout(() => {
                      navigate('/?cart=open');
                    }, 500);
                  }}
                  disabled={product.quantity === 0}
                  className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-lg font-semibold shadow-lg"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Buy Now
                </Button>
              </div>
            </div>

            {/* Additional Info */}
            <div className="pt-6 border-t border-slate-200 space-y-3">
              {product.supplier && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Supplier:</span>
                  <span className="font-medium text-slate-900">{product.supplier}</span>
                </div>
              )}
              {product.barcode && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Barcode:</span>
                  <span className="font-medium text-slate-900">{product.barcode}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Social Media Videos Section */}
        {(product.tiktok_url || product.instagram_url) && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Product Videos</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {/* TikTok Video */}
              {product.tiktok_url && (
                <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden shadow-lg">
                  <div className="aspect-[9/16] max-h-[700px] bg-black flex items-center justify-center">
                    <blockquote 
                      className="tiktok-embed w-full h-full" 
                      cite={product.tiktok_url}
                      data-video-id={product.tiktok_url.split('/video/')[1]?.split('?')[0] || product.tiktok_url.split('/').pop()?.split('?')[0]}
                      style={{ maxWidth: '605px', minWidth: '325px' }}
                    >
                      <section>
                        <a 
                          target="_blank" 
                          rel="noopener noreferrer"
                          href={product.tiktok_url}
                          className="text-white text-center block p-8"
                        >
                          View TikTok Video
                        </a>
                      </section>
                    </blockquote>
                  </div>
                </div>
              )}

              {/* Instagram Video */}
              {product.instagram_url && (
                <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden shadow-lg">
                  <div className="aspect-square max-h-[700px] flex items-center justify-center">
                    <blockquote 
                      className="instagram-media w-full" 
                      data-instgrm-permalink={product.instagram_url}
                      data-instgrm-version="14"
                    >
                      <a 
                        href={product.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-600 text-center block p-8"
                      >
                        View Instagram Post
                      </a>
                    </blockquote>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Reviews Section */}
      {product && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-slate-900">Customer Reviews</h2>
              {productStats && productStats.total_reviews > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-5 h-5 ${
                          star <= Math.round(productStats.average_rating)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-slate-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-lg font-semibold text-slate-900">
                    {productStats.average_rating.toFixed(1)}
                  </span>
                  <span className="text-slate-600">
                    ({productStats.total_reviews} {productStats.total_reviews === 1 ? 'review' : 'reviews'})
                  </span>
                </div>
              )}
            </div>
            <Button
              onClick={() => navigate(`/review/write?productId=${product.id}`)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Write a Review
            </Button>
          </div>

          {reviews.length > 0 ? (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div key={review.id} className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-slate-900">{review.customer_name}</p>
                        {review.verified_purchase && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                            Verified Purchase
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= review.rating
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-slate-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-slate-600">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-slate-700 mb-4">{review.comment}</p>

                  {review.images && review.images.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {review.images.map((image, index) => (
                        <img
                          key={index}
                          src={image}
                          alt={`Review ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90"
                          onClick={() => window.open(image, '_blank')}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <p className="text-slate-600 mb-4">No reviews yet. Be the first to review this product!</p>
              <Button
                onClick={() => navigate(`/review/write?productId=${product.id}`)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Write the First Review
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Share Product Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share Product</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="font-semibold text-slate-900">{product.name}</p>
              <p className="text-sm text-emerald-600">{formatCurrency(product.price)}</p>
            </div>
            
            <div className="space-y-2">
              <Button
                onClick={() => handleShare('whatsapp')}
                className="w-full h-12 bg-green-600 hover:bg-green-700 text-white"
              >
                <Send className="w-5 h-5 mr-2" />
                Share via WhatsApp
              </Button>
              
              <Button
                onClick={() => handleShare('viber')}
                className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Send className="w-5 h-5 mr-2" />
                Share via Viber
              </Button>
              
              <Button
                onClick={() => handleShare('copy')}
                className="w-full h-12 bg-slate-600 hover:bg-slate-700 text-white"
              >
                <Share2 className="w-5 h-5 mr-2" />
                Copy Link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
